import type { GraphMember } from '@/contexts/FamilyGraphContext';

export type TreeBranchPath = string;

const orderedParents = (
  member: GraphMember,
  byId: Map<string, GraphMember>,
): string[] =>
  (member.parentIds ?? [])
    .filter((id) => byId.has(id))
    .slice()
    .sort((a, b) => {
      const left = byId.get(a);
      const right = byId.get(b);

      const rank = (row?: GraphMember) =>
        row?.relationship === 'mother' || row?.side === 'mother'
          ? 0
          : row?.relationship === 'father' || row?.side === 'father'
            ? 1
            : 2;

      const rankDelta = rank(left) - rank(right);
      if (rankDelta !== 0) return rankDelta;

      /*
       * Branch identity must never depend on a mutable display name.
       * Side authority ranks parents first; stable member IDs provide
       * the deterministic tie-break.
       */
      return a.localeCompare(b);
    });

export const computeTreeBranchPaths = (
  members: GraphMember[],
  viewerId: string | null | undefined,
): Map<string, TreeBranchPath> => {
  const paths = new Map<string, TreeBranchPath>();
  const byId = new Map(members.map((member) => [member.id, member]));

  if (!viewerId || !byId.has(viewerId)) return paths;

  paths.set(viewerId, 'self');

  const queue: Array<{ id: string; path: string }> = [];
  const viewer = byId.get(viewerId)!;

  orderedParents(viewer, byId).forEach((parentId, index) => {
    queue.push({
      id: parentId,
      path: `ancestor.${index}`,
    });
  });

  const visited = new Set<string>();

  while (queue.length > 0) {
    const current = queue.shift()!;

    if (visited.has(current.id)) continue;
    visited.add(current.id);

    const existing = paths.get(current.id);
    if (!existing || current.path < existing) {
      paths.set(current.id, current.path);
    }

    const member = byId.get(current.id);
    if (!member) continue;

    orderedParents(member, byId).forEach((parentId, index) => {
      queue.push({
        id: parentId,
        path: `${current.path}.${index}`,
      });
    });
  }

  // Descendants inherit a stable child branch beneath their proven parent.
  let changed = true;
  let pass = 0;

  while (changed && pass++ < members.length + 2) {
    changed = false;

    for (const member of members) {
      if (paths.has(member.id)) continue;

      const parentPaths = (member.parentIds ?? [])
        .map((parentId) => ({
          parentId,
          path: paths.get(parentId),
        }))
        .filter(
          (
            value,
          ): value is {
            parentId: string;
            path: string;
          } => !!value.path,
        )
        .sort((a, b) => a.path.localeCompare(b.path));

      if (parentPaths.length === 0) continue;

      const parentPath = parentPaths[0].path;
      const siblings = members
        .filter((candidate) =>
          (candidate.parentIds ?? []).includes(
            parentPaths[0].parentId,
          ),
        )
        .slice()
        .sort((a, b) => a.id.localeCompare(b.id));

      const siblingIndex = Math.max(
        0,
        siblings.findIndex(
          (candidate) => candidate.id === member.id,
        ),
      );

      paths.set(
        member.id,
        `${parentPath}.child.${siblingIndex}`,
      );

      changed = true;
    }
  }

  // Spouses and partners inherit the connected member's exact branch path.
  for (let passIndex = 0; passIndex < 4; passIndex += 1) {
    let spouseChanged = false;

    for (const member of members) {
      if (paths.has(member.id)) continue;

      const forwardPath = member.spouseId
        ? paths.get(member.spouseId)
        : null;

      if (forwardPath) {
        paths.set(member.id, `${forwardPath}.partner`);
        spouseChanged = true;
        continue;
      }

      const reversePartner = members.find(
        (candidate) =>
          candidate.spouseId === member.id &&
          paths.has(candidate.id),
      );

      if (reversePartner) {
        paths.set(
          member.id,
          `${paths.get(reversePartner.id)!}.partner`,
        );
        spouseChanged = true;
      }
    }

    if (!spouseChanged) break;
  }

  return paths;
};

export const commonTreeBranchPrefix = (
  paths: string[],
): string => {
  if (paths.length === 0) return 'unplaced';

  const split = paths.map((path) => path.split('.'));
  const shortest = Math.min(...split.map((parts) => parts.length));
  const common: string[] = [];

  for (let index = 0; index < shortest; index += 1) {
    const token = split[0][index];

    if (split.every((parts) => parts[index] === token)) {
      common.push(token);
    } else {
      break;
    }
  }

  return common.length > 0 ? common.join('.') : 'mixed';
};
